/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Drive Asset Manager
 * 
 * Central service for managing Drive-backed assets.
 * Handles upload to Drive, thumbnail generation, and HD retrieval.
 */

import { supabase } from './supabaseClient';
import { isDriveConnected } from './googleDriveClient';
import { saveAsset, LocalAssetRecord } from './localAssetDB';

// === TYPES ===

export interface DriveAsset extends LocalAssetRecord {
    webViewLink?: string;
}

export interface UploadResult {
    success: boolean;
    asset?: DriveAsset;
    error?: string;
}

// === CONFIGURATION ===

const THUMBNAIL_MAX_SIZE = 256; // Max dimension for thumbnails
const THUMBNAIL_QUALITY = 0.6; // JPEG quality for thumbnails

// === THUMBNAIL GENERATION ===

/**
 * Generate a compressed thumbnail from an image blob
 */
export async function generateImageThumbnail(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        
        img.onload = () => {
            try {
                let width = img.width;
                let height = img.height;

                // Resize to max dimension
                if (width > THUMBNAIL_MAX_SIZE || height > THUMBNAIL_MAX_SIZE) {
                    if (width > height) {
                        height = Math.round((height * THUMBNAIL_MAX_SIZE) / width);
                        width = THUMBNAIL_MAX_SIZE;
                    } else {
                        width = Math.round((width * THUMBNAIL_MAX_SIZE) / height);
                        height = THUMBNAIL_MAX_SIZE;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);
                const dataUrl = canvas.toDataURL('image/jpeg', THUMBNAIL_QUALITY);
                const base64 = dataUrl.split(',')[1];
                
                URL.revokeObjectURL(url);
                resolve(base64);
            } catch (e) {
                URL.revokeObjectURL(url);
                reject(e);
            }
        };
        
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image for thumbnail'));
        };
        
        img.src = url;
    });
}

/**
 * Generate thumbnail from video (captures middle frame)
 */
export async function generateVideoThumbnail(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const url = URL.createObjectURL(blob);
        
        video.onloadedmetadata = () => {
            // Seek to middle of video
            video.currentTime = video.duration / 2;
        };
        
        video.onseeked = () => {
            try {
                const canvas = document.createElement('canvas');
                let width = video.videoWidth;
                let height = video.videoHeight;

                // Resize to max dimension
                if (width > THUMBNAIL_MAX_SIZE || height > THUMBNAIL_MAX_SIZE) {
                    if (width > height) {
                        height = Math.round((height * THUMBNAIL_MAX_SIZE) / width);
                        width = THUMBNAIL_MAX_SIZE;
                    } else {
                        width = Math.round((width * THUMBNAIL_MAX_SIZE) / height);
                        height = THUMBNAIL_MAX_SIZE;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                ctx.drawImage(video, 0, 0, width, height);
                const dataUrl = canvas.toDataURL('image/jpeg', THUMBNAIL_QUALITY);
                const base64 = dataUrl.split(',')[1];
                
                URL.revokeObjectURL(url);
                resolve(base64);
            } catch (e) {
                URL.revokeObjectURL(url);
                reject(e);
            }
        };
        
        video.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load video for thumbnail'));
        };
        
        video.src = url;
        video.load();
    });
}

// === DRIVE OPERATIONS ===

/**
 * Get auth token for Drive API calls
 */
async function getAuthToken(): Promise<string | null> {
    const session = await supabase?.auth.getSession();
    return session?.data.session?.access_token || null;
}

/**
 * Upload an asset to Google Drive and save to local DB
 */
export async function uploadAssetToDrive(
    blob: Blob,
    filename: string,
    type: 'image' | 'video',
    metadata?: {
        prompt?: string;
        modelId?: string;
        projectId?: string;
    }
): Promise<UploadResult> {
    console.log(`[DriveAssetManager] Starting upload: ${filename} (${type}, ${blob.size} bytes)`);

    // Check if Drive is connected
    const connected = await isDriveConnected();
    if (!connected) {
        console.warn('[DriveAssetManager] Drive not connected, saving locally only');
        return { success: false, error: 'DRIVE_NOT_CONNECTED' };
    }

    try {
        // 1. Generate thumbnail
        console.log('[DriveAssetManager] Generating thumbnail...');
        const thumbnailBase64 = type === 'image'
            ? await generateImageThumbnail(blob)
            : await generateVideoThumbnail(blob);

        // 2. Get auth token
        const token = await getAuthToken();
        if (!token) {
            return { success: false, error: 'NOT_AUTHENTICATED' };
        }

        // 3. Initialize resumable upload
        const initRes = await fetch('/api/google/drive/init-upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                fileName: filename,
                mimeType: type === 'image' ? 'image/jpeg' : 'video/mp4',
                folderName: type === 'image' ? 'images' : 'videos'
            })
        });

        if (!initRes.ok) {
            const err = await initRes.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to init upload');
        }

        const { uploadUrl, folderId } = await initRes.json();
        if (!uploadUrl) throw new Error('No upload URL received');

        // 4. Upload to Drive
        console.log('[DriveAssetManager] Uploading to Drive...');
        const uploadRes = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': type === 'image' ? 'image/jpeg' : 'video/mp4',
                'Content-Length': blob.size.toString()
            },
            body: blob
        });

        if (!uploadRes.ok) {
            throw new Error(`Drive upload failed: ${uploadRes.status}`);
        }

        const driveFile = await uploadRes.json();
        console.log('[DriveAssetManager] Upload complete:', driveFile.id);

        // 5. Create asset record
        const assetId = `asset_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const asset: DriveAsset = {
            id: assetId,
            type,
            driveFileId: driveFile.id,
            thumbnailBase64,
            filename,
            prompt: metadata?.prompt,
            modelId: metadata?.modelId,
            projectId: metadata?.projectId,
            createdAt: new Date().toISOString(),
            size: blob.size,
            webViewLink: driveFile.webViewLink
        };

        // 6. Save to IndexedDB
        await saveAsset(asset);
        console.log('[DriveAssetManager] Asset saved to local DB:', assetId);

        return { success: true, asset };

    } catch (error) {
        console.error('[DriveAssetManager] Upload error:', error);
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
        };
    }
}

/**
 * Fetch HD asset from Drive by file ID
 */
export async function fetchHDFromDrive(driveFileId: string): Promise<Blob | null> {
    console.log('[DriveAssetManager] Fetching HD:', driveFileId);

    try {
        const token = await getAuthToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const res = await fetch(`/api/google/drive/download/${driveFileId}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!res.ok) {
            throw new Error(`Download failed: ${res.status}`);
        }

        return await res.blob();
    } catch (error) {
        console.error('[DriveAssetManager] Fetch error:', error);
        return null;
    }
}

/**
 * Get Drive web view link for an asset
 */
export function getDriveViewLink(driveFileId: string): string {
    return `https://drive.google.com/file/d/${driveFileId}/view`;
}

/**
 * Convert blob to ImageFile format (for compatibility with existing code)
 */
export function blobToImageFile(blob: Blob, filename: string): Promise<{ file: File; base64: string }> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            const file = new File([blob], filename, { type: blob.type });
            resolve({ file, base64 });
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
